// Typed REST client for the bluerock-selfhosted API.
//
// Built on openapi-fetch + the generated schema.ts. Every call gets
// compile-time-checked path, method, params, body, and response types
// — schema drift between API and UI surfaces as a TypeScript error.
//
// Cross-cutting concerns:
//   - Bearer token (when present) on every request
//   - Idempotency-Key on every mutating request — minted at the call
//     site by the page (StrictMode-safe; sec review L2 2026-05-27).
//
// In production this client will negotiate an OIDC access token via the
// AuthProvider; today the AuthProvider returns null and we run against
// the API's --dev-auth-stub which accepts any caller as dev@localhost.
import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./gen/schema";
import { useAuth } from "@/lib/auth/AuthProvider";

const BASE_URL = import.meta.env.BASE_URL + "api/v1";

/** UUID v4 for an Idempotency-Key.
 *
 * Always uses platform crypto — never Math.random.
 *
 * Falls back to crypto.getRandomValues() when crypto.randomUUID() is
 * unavailable. randomUUID requires a SECURE CONTEXT (HTTPS or
 * localhost) per the WebCrypto spec; getRandomValues works in any
 * context. Serving the UI over plain HTTP (the v0 smoke path) hit
 * this — every mutating action threw "crypto.randomUUID is not a
 * function" and silently failed. Real fix is HTTPS; this fallback
 * keeps non-TLS dev usable. */
export function newIdempotencyKey(): string {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10 (RFC 4122)
  const h = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20, 32)}`;
}

/** Construct a typed client, optionally with a bearer token. Use the
 *  React hook `useApi()` below in components rather than calling this
 *  directly; the hook reads the auth context for token + handles the
 *  Bearer header through middleware. */
function buildClient(token: string | null) {
  const client = createClient<paths>({ baseUrl: BASE_URL });

  const authMiddleware: Middleware = {
    async onRequest({ request }) {
      if (token) request.headers.set("Authorization", `Bearer ${token}`);
      return request;
    },
  };
  client.use(authMiddleware);
  return client;
}

/** React hook: typed API client wired to the current auth identity.
 *  Memoization is on `token`, so a token change rebuilds the client.
 *  On any 401 from the API, calls onUnauthorized() so the AuthProvider
 *  drops the token and the router redirects to /login. */
export function useApi() {
  const { token, onUnauthorized } = useAuth();
  const client = buildClient(token);
  client.use({
    async onResponse({ response }) {
      if (response.status === 401) onUnauthorized();
      return response;
    },
  });
  return client;
}

/** A common UI pattern: extract a flattened error message from the
 *  openapi-fetch `{error}` payload. Falls back to status code.
 *
 *  Appends `request_id` when the API sent one (also echoed as the
 *  `X-Request-Id` response header) — 500 bodies are deliberately generic
 *  (the real detail is server-log-only), so this is the only thing that
 *  lets a user hand support something to correlate against. Cheap to
 *  include on every status, not just 500s. */
export function describeError(err: unknown, status?: number): string {
  if (!err) return status ? `Request failed (${status})` : "Request failed";
  if (typeof err === "object" && err !== null) {
    const p = err as { detail?: string; title?: string; code?: string; request_id?: string };
    const message = p.detail || p.title || p.code || JSON.stringify(err);
    return p.request_id ? `${message} (ref: ${p.request_id})` : message;
  }
  return String(err);
}
